/**
 * Shared step card primitives for API sequence views.
 *
 * Reused by both the read-only ApiSequenceView and the editable SortableStepCard.
 *
 * @module node-config/StepCard
 */

import React from 'react';
import { CaretDown, CaretRight } from '@phosphor-icons/react';
import { Text, TagBadge } from '@chatsdet/ui';
import type { ApiSequenceStep } from '@chatsdet/types';
import { CopyablePre } from '../CopyButton';

/** Colour map for HTTP method badges */
const METHOD_COLORS: Record<string, string> = {
  GET: 'text-blue-500 bg-blue-500/10',
  POST: 'text-green-500 bg-green-500/10',
  PUT: 'text-amber-500 bg-amber-500/10',
  PATCH: 'text-orange-500 bg-orange-500/10',
  DELETE: 'text-red-500 bg-red-500/10',
};

/** Coloured HTTP method pill */
const MethodBadge: React.FC<{ method: string }> = ({ method }) => {
  const upper = method.toUpperCase();
  const colorClass = METHOD_COLORS[upper] ?? 'text-foreground bg-muted';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-mono font-semibold ${colorClass}`}>
      {upper}
    </span>
  );
};

/** Outer card wrapper shared by read-only and editable step cards */
export const StepCardShell: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div className={`border rounded-lg bg-card shadow-sm overflow-hidden ${className ?? ''}`}>
    {children}
  </div>
);

/** Header bar for a step card. Renders step number badge + left/right slots. */
export const StepCardHeader: React.FC<{
  stepNumber: number;
  stepId?: string;
  left?: React.ReactNode;
  right?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}> = ({ stepNumber, stepId, left, right, onClick, className }) => {
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      type={onClick ? 'button' : undefined}
      className={`w-full flex items-center justify-between px-4 py-3 bg-muted/30 border-b ${onClick ? 'cursor-pointer hover:bg-muted/50 transition-colors' : ''} ${className ?? ''}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-2">
        {left}
        <TagBadge name={<>Step {stepNumber}</>} color="gray" hideDot size="sm" />
        {stepId && (
          <Text size="xs" className="text-muted-foreground font-mono">
            {stepId}
          </Text>
        )}
      </div>
      {right && <div className="flex items-center gap-1">{right}</div>}
    </Wrapper>
  );
};

/** Clickable header row for read-only step cards with expand/collapse caret */
export const CollapsibleStepRow: React.FC<{
  step: ApiSequenceStep;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ step, index, isExpanded, onToggle }) => {
  const method = (step.method ?? 'GET').toUpperCase();
  return (
    <button
      type="button"
      className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-muted/50 transition-colors min-w-0 overflow-hidden"
      onClick={onToggle}
    >
      <span className="text-muted-foreground flex-shrink-0">
        {isExpanded ? <CaretDown size={14} /> : <CaretRight size={14} />}
      </span>
      <TagBadge name={index + 1} color="gray" hideDot size="sm" className="flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">
          {step.description ?? step.urlTemplate ?? '—'}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <MethodBadge method={method} />
          <span
            className="text-xs font-mono truncate text-muted-foreground min-w-0"
            title={step.urlTemplate ?? undefined}
          >
            {step.urlTemplate ?? '—'}
          </span>
        </div>
      </div>
      {step.expectedStatusCodes && step.expectedStatusCodes.length > 0 && (
        <span className="text-xs text-muted-foreground flex-shrink-0">
          expects {step.expectedStatusCodes.join(', ')}
        </span>
      )}
    </button>
  );
};

/** Read-only detail section for an expanded step */
export const StepDetailSection: React.FC<{ step: ApiSequenceStep }> = ({ step }) => (
  <div className="px-4 py-3 border-t border-border bg-muted/30 space-y-3">
    {step.description && (
      <div>
        <Text size="xs" weight="semibold" className="text-muted-foreground uppercase mb-1">
          Description
        </Text>
        <Text size="sm">{step.description}</Text>
      </div>
    )}
    {step.bodyTemplate && (() => {
      const bodyText = tryFormatJson(step.bodyTemplate);
      return (
        <div>
          <Text size="xs" weight="semibold" className="text-muted-foreground uppercase mb-1">
            Body
          </Text>
          <CopyablePre value={bodyText} label="body">
            <pre className="text-xs font-mono bg-background p-2 rounded border border-border overflow-x-auto max-h-40">
              {bodyText}
            </pre>
          </CopyablePre>
        </div>
      );
    })()}
    {step.headersTemplate && Object.keys(step.headersTemplate).length > 0 && (() => {
      const headersText = JSON.stringify(step.headersTemplate, null, 2);
      return (
        <div>
          <Text size="xs" weight="semibold" className="text-muted-foreground uppercase mb-1">
            Headers
          </Text>
          <CopyablePre value={headersText} label="headers">
            <pre className="text-xs font-mono bg-background p-2 rounded border border-border overflow-x-auto">
              {headersText}
            </pre>
          </CopyablePre>
        </div>
      );
    })()}
    {step.responseVariableName && (
      <div>
        <Text size="xs" className="text-muted-foreground">
          Response stored as <code className="text-primary">{step.responseVariableName}</code>
        </Text>
      </div>
    )}
    {step.validations && step.validations.length > 0 && (
      <div>
        <Text size="xs" weight="semibold" className="text-muted-foreground uppercase mb-1">
          Validations
        </Text>
        <div className="space-y-1">
          {step.validations.map((v, i) => (
            <code
              key={i}
              className="block text-xs font-mono bg-background p-1.5 rounded border border-border"
            >
              {v.left} {v.operator}
              {v.right !== null && v.right !== undefined ? ` ${v.right}` : ''}
            </code>
          ))}
        </div>
      </div>
    )}
  </div>
);

function tryFormatJson(str: string): string {
  try {
    return JSON.stringify(JSON.parse(str), null, 2);
  } catch {
    return str;
  }
}
